import { computed, ref } from 'vue';
import type { ComputedRef, Ref } from 'vue';

import type { AccountUser, ResolvedAccountLink } from './types';
import { ACCOUNT_MENU_LINKS } from '../lib/accountMenu';
import { buildAccountHref } from '../lib/buildAccountHref';

export interface UseHeaderAccount {
  user: Ref<AccountUser | null>;
  accountUrl: Ref<string>;

  isAuthorized: ComputedRef<boolean>;
  displayName: ComputedRef<string>;
  resolvedLinks: ComputedRef<ResolvedAccountLink[]>;

  setUser: (u: AccountUser | null) => void;
  setAccountUrl: (url: string) => void;
}

export interface UseHeaderAccountOptions {
  initialUser?: AccountUser | null;
  initialAccountUrl?: string;
}

export function useHeaderAccount(options: UseHeaderAccountOptions = {}): UseHeaderAccount {
  const user = ref<AccountUser | null>(options.initialUser ?? null);
  const accountUrl = ref<string>(options.initialAccountUrl ?? '');

  const isAuthorized = computed<boolean>(() => user.value != null);

  const displayName = computed<string>(() => {
    const name = user.value?.name;
    const email = user.value?.email;
    const display = name || email;
    if(!display) return '';

    return `${display[0]}${display[1]}`;
  });

  const resolvedLinks = computed<ResolvedAccountLink[]>(() =>
    ACCOUNT_MENU_LINKS.map((link) => ({
      ...link,
      href: buildAccountHref(accountUrl.value, link.path),
    })),
  );

  const setUser = (u: AccountUser | null): void => {
    user.value = u;
  };

  const setAccountUrl = (url: string): void => {
    accountUrl.value = url;
  };

  return {
    user,
    accountUrl,
    isAuthorized,
    displayName,
    resolvedLinks,
    setUser,
    setAccountUrl,
  };
}
